import styled from '@emotion/styled';
import { Stack, FieldList, Radio, NumberInput, tokens } from '@hotjar/hotsauce';
import type { ChangeEvent } from 'react';
import { useCallback, useState, useEffect } from 'react';
import type {
  DisplayCondition,
  PersistCondition,
} from '@hotjar/feedback/domain/Surveys/Survey/Behavior';
import { useTranslationWithPrefix } from '@hotjar/translations';

const InlineBlock = styled.div`
  display: inline-block;
`;

const Container = styled(Stack)`
  /* override insights styles */
  label {
    margin: 0;
  }
  /* end overrides */
`;

const StyledNumberInput = styled(NumberInput)`
  margin: 0 ${tokens.spacer.x1};
  width: 80px;
`;

const DisplayInputWrapper = styled.div`
  ${tokens.text('body2')}
  color: ${tokens.color.text_light};
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  margin-top: ${tokens.spacer.x0};
  margin-left: ${tokens.spacer.x3};
`;

const ValidationError = styled.span`
  ${tokens.text('caption')}
  color: ${tokens.color.text_error};
  margin-top: ${tokens.spacer.x1};
  margin-left: ${tokens.spacer.x3};
`;

function numberOrUndefined(value: number) {
  return Number.isNaN(value) ? undefined : value;
}

/* Tracks the state of a string and returns a valid number or undefined */
const useValidNumber = (
  value: number | undefined,
  onValueChanged: (newValue: number | undefined) => void,
): [string, (event: ChangeEvent<HTMLInputElement>) => void] => {
  const [numberInputValue, setNumberInputValue] = useState<string>(
    value === undefined ? '' : value.toString(),
  );

  useEffect(() => {
    if (value === undefined) return;
    setNumberInputValue(`${value}`);
  }, [value]);

  useEffect(() => {
    const newValue = numberOrUndefined(parseFloat(numberInputValue));
    if (newValue === value) return;
    onValueChanged(newValue);
  }, [numberInputValue, onValueChanged, value]);

  const onInputChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setNumberInputValue(event.target.value);
  };

  return [numberInputValue, onInputChanged];
};

export interface BehaviorSectionProps {
  isOffsite: boolean;
  displayCondition: DisplayCondition;
  displayDelay: number | undefined;
  onDisplayConditionChanged: (displayCondition: DisplayCondition) => void;
  onDisplayDelayChanged: (displayDelay: number | undefined) => void;
  persistCondition: PersistCondition;
  onPersistConditionChanged: (persistCondition: PersistCondition) => void;
  isAbandonDisabled: boolean;
  showDisplaySettings: boolean;
}

export const BehaviorSection = ({
  isOffsite,
  showDisplaySettings,
  displayCondition,
  displayDelay,
  onDisplayConditionChanged,
  onDisplayDelayChanged,
  persistCondition,
  onPersistConditionChanged,
  isAbandonDisabled,
}: BehaviorSectionProps): JSX.Element => {
  const { t } = useTranslationWithPrefix('feedback:wizard.section.behavior');

  const displayConditionChanged = useCallback(
    (newDisplayCondition: DisplayCondition) => {
      onDisplayConditionChanged(newDisplayCondition);
      if (newDisplayCondition !== 'delay' && displayDelay !== undefined) {
        onDisplayDelayChanged(undefined);
      }
    },
    [onDisplayConditionChanged, onDisplayDelayChanged, displayDelay],
  );

  const isDelayValid = displayDelay !== undefined && displayDelay >= 0 && displayDelay <= 600;

  const [currentDisplayDelayValue, onCurrentDisplayDelayValueChanged] = useValidNumber(
    displayDelay,
    onDisplayDelayChanged,
  );

  const entityLabelForDisplaySettings = isOffsite
    ? t('show.entity.invitation')
    : t('show.entity.survey');

  return (
    <Container preset="form">
      {showDisplaySettings && (
        <FieldList
          size="l"
          title={t('show.title', { entityLabelForDisplaySettings })}
          helperText={t('show.description', { entityLabelForDisplaySettings })}
        >
          <Radio
            name="display-value"
            isChecked={displayCondition === 'immediate'}
            onChange={() => displayConditionChanged('immediate')}
            labelContent={t('show.option.immediate')}
          />

          <Stack direction="column" spacing="x0">
            <Radio
              name="display-value"
              isChecked={displayCondition === 'delay'}
              onChange={() => displayConditionChanged('delay')}
              labelContent={t('show.option.delay.title')}
              isInvalid={displayCondition === 'delay' && !isDelayValid}
            />
            {displayCondition === 'delay' && (
              <Stack direction="column" spacing="x0">
                <DisplayInputWrapper>
                  <span>{t('show.option.delay.description-1')}</span>
                  <InlineBlock>
                    <StyledNumberInput
                      aria-label={t('show.option.delay.textbox-label')}
                      value={currentDisplayDelayValue}
                      onChange={onCurrentDisplayDelayValueChanged}
                      isInvalid={!isDelayValid}
                      isDisabled={displayCondition !== 'delay'}
                      isRequired
                      step={1}
                    />
                  </InlineBlock>
                  <span>{t('show.option.delay.description-2')}</span>
                </DisplayInputWrapper>
                {!isDelayValid && (
                  <ValidationError>{t('show.option.delay.validation-error')}</ValidationError>
                )}
              </Stack>
            )}
          </Stack>
          <Radio
            name="display-value"
            isDisabled={isAbandonDisabled}
            isChecked={displayCondition === 'abandon'}
            onChange={() => displayConditionChanged('abandon')}
            labelContent={t('show.option.abandon.title')}
            helperText={t('show.option.abandon.description', {
              entityLabelForDisplaySettings,
            })}
          />
          <Radio
            name="display-value"
            isChecked={displayCondition === 'scroll'}
            onChange={() => displayConditionChanged('scroll')}
            labelContent={t('show.option.scroll')}
          />
        </FieldList>
      )}

      <FieldList size="l" title={t('frequency.title')} helperText={t('frequency.description')}>
        <Radio
          name="persist-value"
          isChecked={persistCondition === 'response'}
          onChange={() => onPersistConditionChanged('response')}
          labelContent={t('frequency.option.response')}
        />
        <Radio
          name="persist-value"
          isChecked={persistCondition === 'once'}
          onChange={() => onPersistConditionChanged('once')}
          labelContent={t('frequency.option.once')}
        />
        <Radio
          name="persist-value"
          isChecked={persistCondition === 'always'}
          onChange={() => onPersistConditionChanged('always')}
          labelContent={t('frequency.option.always')}
        />
      </FieldList>
    </Container>
  );
};
